import React, { useContext, useState } from 'react';
import { Outlet, Link, useLocation } from 'react-router-dom';
import { AuthContext } from '@/App';
import { Button } from '@/components/ui/button';
import {
  Home,
  MapPin,
  Package,
  Box,
  Route,
  BarChart3,
  LogOut,
  Menu,
  X,
  User,
  Clock,
  ChevronDown,
  ChevronRight,
  Archive,
  Warehouse,
  Wrench,
  Database,
  Shield,
  Users,
  FileText
} from 'lucide-react';

const Layout = () => {
  const { user, logout } = useContext(AuthContext);
  const location = useLocation();
  const [mobileMenuOpen, setMobileMenuOpen] = React.useState(false);
  const [lagerMenuOpen, setLagerMenuOpen] = useState(false);
  const [adminMenuOpen, setAdminMenuOpen] = useState(false);
  const isAdmin = user?.role === 'admin';
  const isHelper = user?.role === 'helper';

  // ============================================================
  // FEATURE FLAGS - Temporär ausgeblendete Features
  // ============================================================
  const FEATURE_FLAGS = {
    TOURENPLANUNG_ENABLED: false,
  };

  // Navigation Items
  const navigation = [
    { name: 'Dashboard', href: '/', icon: Home, roles: ['admin', 'helper'] },
    { name: 'Standorte', href: '/standorte', icon: MapPin, roles: ['admin'] },
    { name: 'Automaten', href: '/automaten', icon: Box, roles: ['admin', 'helper'] },
    { name: 'Produkte', href: '/produkte', icon: Package, roles: ['admin'] },
    { name: 'Werkstatt Inventar', href: '/werkstatt-inventar', icon: Wrench, roles: ['helper'] },
    { name: 'Tourenplanung', href: '/tourenplanung', icon: Route, roles: ['admin'], showIf: FEATURE_FLAGS.TOURENPLANUNG_ENABLED },
    { name: 'Zeiterfassung', href: '/zeiterfassung', icon: Clock, roles: ['admin', 'helper'] },
    { name: 'Auswertungen', href: '/reports', icon: BarChart3, roles: ['admin'] },
    { name: 'Kassenbuch', href: '/kassenbuch', icon: BarChart3, roles: ['admin'] },
    { name: 'Mein Konto', href: '/mein-konto', icon: User, roles: ['admin', 'helper'] },
  ];

  // Lager-Submenu Items (nur Admin)
  const lagerSubmenu = [
    { name: 'Lagerbestände', href: '/lager', icon: Warehouse, roles: ['admin'] },
    { name: 'Inventar', href: '/inventar', icon: Archive, roles: ['admin'] },
  ];

  // Admin-Submenu Items (nur Admin)
  const adminSubmenu = [
    { name: 'Nutzerverwaltung', href: '/users', icon: Users, roles: ['admin'] },
    { name: 'Backups', href: '/backups', icon: Database, roles: ['admin'] },
    { name: 'Versionen', href: '/release-notes', icon: FileText, roles: ['admin'] },
  ];

  const filteredNav = navigation.filter(item => {
    if (item.showIf !== undefined) {
      return item.showIf;
    }
    return item.roles.includes(user?.role);
  });
  const filteredLagerSubmenu = lagerSubmenu.filter(item => item.roles.includes(user?.role));
  const filteredAdminSubmenu = adminSubmenu.filter(item => item.roles.includes(user?.role));
  const showLagerMenu = isAdmin;
  const showAdminMenu = isAdmin; // Admin-Menü nur für Admins

  // Prüfe ob aktueller Pfad im Lager-Bereich ist
  const isLagerActive = location.pathname === '/lager' || location.pathname === '/inventar';
  
  // Prüfe ob aktueller Pfad im Admin-Bereich ist
  const isAdminActive = location.pathname === '/users' || location.pathname === '/backups' || location.pathname === '/release-notes';

  // Render Navigation Item
  const renderNavItem = (item, isMobile = false) => {
    const Icon = item.icon;
    const isActive = location.pathname === item.href;
    return (
      <Link
        key={item.name}
        to={item.href}
        onClick={() => isMobile && setMobileMenuOpen(false)}
        className={`flex items-center space-x-3 px-4 py-3 rounded-lg transition-all ${
          isActive
            ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg'
            : 'text-slate-700 hover:bg-slate-100'
        }`}
        data-testid={`${isMobile ? 'mobile-' : ''}nav-${item.name.toLowerCase().replace(' ', '-')}`}
      >
        <Icon size={20} />
        <span className="font-medium">{item.name}</span>
      </Link>
    );
  };

  // Render Lager Dropdown
  const renderLagerDropdown = (isMobile = false) => {
    if (!showLagerMenu) return null;

    return (
      <div className="space-y-1">
        <button
          onClick={() => setLagerMenuOpen(!lagerMenuOpen)}
          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
            isLagerActive
              ? 'bg-gradient-to-r from-blue-500 to-indigo-500 text-white shadow-lg'
              : 'text-slate-700 hover:bg-slate-100'
          }`}
          data-testid={`${isMobile ? 'mobile-' : ''}nav-lager`}
        >
          <div className="flex items-center space-x-3">
            <Package size={20} />
            <span className="font-medium">Lager</span>
          </div>
          {lagerMenuOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        
        {lagerMenuOpen && (
          <div className="ml-4 space-y-1 border-l-2 border-slate-200 pl-2">
            {filteredLagerSubmenu.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => isMobile && setMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-all text-sm ${
                    isActive
                      ? 'bg-blue-100 text-blue-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`${isMobile ? 'mobile-' : ''}nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon size={16} />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  // Render Admin Dropdown (nur für Admins sichtbar)
  const renderAdminDropdown = (isMobile = false) => {
    if (!showAdminMenu) return null;

    return (
      <div className="space-y-1">
        <button
          onClick={() => setAdminMenuOpen(!adminMenuOpen)}
          className={`w-full flex items-center justify-between px-4 py-3 rounded-lg transition-all ${
            isAdminActive
              ? 'bg-gradient-to-r from-purple-500 to-indigo-500 text-white shadow-lg'
              : 'text-slate-700 hover:bg-slate-100'
          }`}
          data-testid={`${isMobile ? 'mobile-' : ''}nav-admin`}
        >
          <div className="flex items-center space-x-3">
            <Shield size={20} />
            <span className="font-medium">Admin</span>
          </div>
          {adminMenuOpen ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
        </button>
        
        {adminMenuOpen && (
          <div className="ml-4 space-y-1 border-l-2 border-purple-200 pl-2">
            {filteredAdminSubmenu.map((item) => {
              const Icon = item.icon;
              const isActive = location.pathname === item.href;
              return (
                <Link
                  key={item.name}
                  to={item.href}
                  onClick={() => isMobile && setMobileMenuOpen(false)}
                  className={`flex items-center space-x-3 px-3 py-2 rounded-lg transition-all text-sm ${
                    isActive
                      ? 'bg-purple-100 text-purple-700 font-medium'
                      : 'text-slate-600 hover:bg-slate-100'
                  }`}
                  data-testid={`${isMobile ? 'mobile-' : ''}nav-${item.name.toLowerCase().replace(' ', '-')}`}
                >
                  <Icon size={16} />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50">
      {/* Header */}
      <header className="bg-white/80 backdrop-blur-md border-b border-slate-200 sticky top-0 z-50">
        <div className="max-w-7xl mx-auto px-2 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center h-14 sm:h-16">
            {/* Linke Seite: Hamburger + Logo + Version */}
            <div className="flex items-center min-w-0 flex-shrink-0">
              <button
                onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                className="lg:hidden p-1.5 sm:p-2 rounded-lg hover:bg-slate-100 flex-shrink-0"
                data-testid="mobile-menu-button"
              >
                {mobileMenuOpen ? <X size={22} /> : <Menu size={22} />}
              </button>
              <div className="flex items-center ml-1 sm:ml-3 min-w-0">
                <img 
                  src="/vivi_logo.png" 
                  alt="VIVI Logo" 
                  className="h-8 sm:h-10 w-auto flex-shrink-0"
                  data-testid="company-logo"
                />
                <span className="ml-2 text-xs text-slate-400 whitespace-nowrap hidden xs:inline"
                      data-testid="app-version">
                  Version 1.6
                </span>
              </div>
            </div>
            
            {/* Rechte Seite: User Info + Logout */}
            <div className="flex items-center space-x-1 sm:space-x-3 flex-shrink-0">
              <div className="text-right hidden sm:block" data-testid="user-info">
                <p className="text-sm font-medium text-slate-900 truncate max-w-[120px] lg:max-w-none">{user?.name}</p>
                <p className="text-xs text-slate-500">
                  {user?.role === 'admin' ? 'Administrator' : 'Helfer'}
                </p>
              </div>
              {/* Mobile: Nur Icon */}
              <div className="sm:hidden text-xs text-slate-500 px-1">
                <User size={18} className="text-slate-400" />
              </div>
              <Button
                onClick={logout}
                variant="outline"
                size="sm"
                className="flex items-center space-x-1 sm:space-x-2 px-2 sm:px-3 h-8 sm:h-9 text-xs sm:text-sm"
                data-testid="logout-button"
              >
                <LogOut size={14} className="sm:w-4 sm:h-4" />
                <span className="hidden sm:inline">Abmelden</span>
              </Button>
            </div>
          </div>
        </div>
      </header>

      <div className="flex max-w-7xl mx-auto">
        {/* Sidebar - Desktop */}
        <aside className="hidden lg:block w-64 min-h-[calc(100vh-3.5rem)] sm:min-h-[calc(100vh-4rem)] bg-white/60 backdrop-blur-sm border-r border-slate-200 p-4">
          <nav className="space-y-2" data-testid="desktop-nav">
            {filteredNav.slice(0, 4).map((item) => renderNavItem(item, false))}
            
            {/* Lager Dropdown nach Produkte */}
            {renderLagerDropdown(false)}
            
            {/* Weitere Navigation Items (ohne "Mein Konto") */}
            {filteredNav.slice(4, -1).map((item) => renderNavItem(item, false))}
            
            {/* Admin Dropdown (nur für Admins) */}
            {renderAdminDropdown(false)}
            
            {/* "Mein Konto" als letztes */}
            {filteredNav.slice(-1).map((item) => renderNavItem(item, false))}
          </nav>
        </aside>

        {/* Mobile Menu */}
        {mobileMenuOpen && (
          <div className="lg:hidden fixed inset-0 z-40 bg-black/50" onClick={() => setMobileMenuOpen(false)}>
            <aside
              className="absolute left-0 top-14 sm:top-16 bottom-0 w-64 max-w-[85vw] bg-white p-3 sm:p-4 shadow-xl overflow-y-auto"
              onClick={(e) => e.stopPropagation()}
              data-testid="mobile-nav"
            >
              {/* User Info in Mobile Menu */}
              <div className="sm:hidden mb-4 pb-3 border-b border-slate-200">
                <p className="text-sm font-medium text-slate-900">{user?.name}</p>
                <p className="text-xs text-slate-500">
                  {user?.role === 'admin' ? 'Administrator' : 'Helfer'}
                </p>
              </div>
              <nav className="space-y-1 sm:space-y-2">
                {filteredNav.slice(0, 4).map((item) => renderNavItem(item, true))}
                
                {/* Lager Dropdown nach Produkte */}
                {renderLagerDropdown(true)}
                
                {/* Weitere Navigation Items (ohne "Mein Konto") */}
                {filteredNav.slice(4, -1).map((item) => renderNavItem(item, true))}
                
                {/* Admin Dropdown (nur für Admins) */}
                {renderAdminDropdown(true)}
                
                {/* "Mein Konto" als letztes */}
                {filteredNav.slice(-1).map((item) => renderNavItem(item, true))}
              </nav>
            </aside>
          </div>
        )}

        {/* Main Content - GLOBALER MOBILE SCROLL CONTAINER */}
        <main className="flex-1 min-w-0 p-2 sm:p-4 lg:p-8 overflow-x-hidden mobile-main-content">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default Layout;
